import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button as MuiButton, IconButton, Stack, Typography, useTheme } from "@mui/material";
import {
  AddCircleOutlineOutlined,
  ArrowBackOutlined,
  DoDisturbAltOutlined,
  ModeOutlined,
  PersonOutlined,
  TrackChanges,
  TuneOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import Header from "../../common/Header";
import CustomTable from "../../common/CustomTable";
import CustomIconButton from "../../common/CustomIconButton";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import AccessRestrictedComponent from "../../common/AccessRestrictedComponent";
import ProductTabNav, { ProductTabItem } from "../product/ProductTabNav";
import { useModalStyles } from "../../common/useModalStyles";
import { useNavigation } from "../../../hooks/useNavigation";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { canAccessIsrByEmail, isIsrAccessReady } from "../../../utils/isrAccess";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { fetchSalesGoalList, saveSalesGoal, updateSalesGoal, useSalesGoalReducer } from "../../../redux/slice/isr/salesGoalSlice";
import {
  fetchSalesConfigList,
  saveSalesConfig,
  updateSalesConfig,
  useSalesConfigReducer,
} from "../../../redux/slice/isr/salesConfigSlice";
import { fetchIsrList, fetchIsrUserEmailList, useIsrReducer } from "../../../redux/slice/isr/isrSlice";
import { CreateSearchAgency, editAgency, inactivateAgencyById, saveAgency } from "../../../redux/slice/agency/AgencySlice";
import { getIsrCommissionTypeKey } from "../../../utils/Utility";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import IsrPopup from "./IsrPopup";
import SalesConfigPopup from "./SalesConfigPopup";
import SalesGoalPopup from "./SalesGoalPopup";
import SalesConfigTargetRatesPopup from "./SalesConfigTargetRatesPopup";
import {
  IsrFormValues,
  IsrRecord,
  IsrTabId,
  SalesConfigFormValues,
  SalesConfigRecord,
  SalesConfigTargetRatesFormValues,
  SalesGoalFormValues,
  SalesGoalRecord,
  getIsrSaveErrorMessage,
  validateCompPercent,
  validateSalesConfigTargetRange,
  validateSalesConfigTargetRangeListNoOverlap,
} from "./isrTypes";

const formatCurrency = (value?: string) => {
  if (!value && value !== "0") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const formatPercent = (value?: string) => {
  if (!value && value !== "0") return "—";
  return `${value}%`;
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
};

const IsrManagement: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { navigate } = useNavigation();
  const { tokens: modalTokens, iconBoxSx, primaryBtnSx } = useModalStyles();
  const { userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const isrAccessReady = isIsrAccessReady(allCodeTablesValuesMap, allCodeTableValueFetchStatus);
  const canAccessIsr = canAccessIsrByEmail(allCodeTablesValuesMap, userInfo?.userName);
  const dispatch = useAppDispatch();
  const { salesGoalList, fetchSalesGoalListStatus } = useSalesGoalReducer();
  const {
    salesConfigList,
    fetchSalesConfigListStatus,
  } = useSalesConfigReducer();
  const { isrList: isrAgencyList, fetchIsrListStatus } = useIsrReducer();

  const [activeTab, setActiveTab] = useState<IsrTabId>("isr");
  const [isrList, setIsrList] = useState<IsrRecord[]>([]);
  const [isrPopupOpen, setIsrPopupOpen] = useState(false);
  const [salesConfigPopupOpen, setSalesConfigPopupOpen] = useState(false);
  const [salesGoalPopupOpen, setSalesGoalPopupOpen] = useState(false);
  const [selectedIsr, setSelectedIsr] = useState<IsrRecord | null>(null);
  const [selectedSalesConfig, setSelectedSalesConfig] = useState<SalesConfigRecord | null>(null);
  const [salesConfigTargetPopupOpen, setSalesConfigTargetPopupOpen] = useState(false);
  const [selectedSalesConfigForTarget, setSelectedSalesConfigForTarget] = useState<SalesConfigRecord | null>(null);
  const [selectedSalesGoal, setSelectedSalesGoal] = useState<SalesGoalRecord | null>(null);
  const [optionPopup, setOptionPopup] = useState<OptionPopupObjectProps>({ message: "", show: false });

  useEffect(() => {
    if (!salesConfigTargetPopupOpen) {
      return;
    }
    const configId = selectedSalesConfigForTarget?.salesConfigId;
    if (configId == null) {
      return;
    }
    const fresh = salesConfigList.find((item) => item.salesConfigId === configId);
    if (fresh) {
      setSelectedSalesConfigForTarget(fresh);
    }
  }, [salesConfigList, salesConfigTargetPopupOpen, selectedSalesConfigForTarget?.salesConfigId]);

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";
  const tableSx = {
    border: `1px solid ${modalTokens.border}`,
    borderRadius: "10px",
    overflow: "hidden",
    p: 0,
    "& .MuiTableContainer-root": { borderRadius: "10px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${listBorderColor}`,
      py: 1.15,
      fontSize: "0.84rem",
      lineHeight: 1.35,
      verticalAlign: "middle",
      whiteSpace: "nowrap",
    },
    "& .MuiTableCell-root .MuiTypography-root": {
      margin: 0,
      lineHeight: 1.35,
      display: "inline",
    },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    py: 0.8,
    px: 1.6,
    fontSize: "0.82rem",
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const isrCommissionTypeKey = useMemo(
    () => getIsrCommissionTypeKey(allCodeTablesValuesMap),
    [allCodeTablesValuesMap]
  );

  const tabs: ProductTabItem[] = useMemo(() => [
    { id: "isr", label: "ISR", icon: <PersonOutlined fontSize="small" /> },
    { id: "salesConfig", label: "Sales Config", icon: <TuneOutlined fontSize="small" /> },
    { id: "salesGoal", label: "Sales Goal", icon: <TrackChanges fontSize="small" /> },
  ], []);

  useEffect(() => {
    if (!canAccessIsr) {
      return;
    }
    dispatch(fetchIsrUserEmailList());
  }, [canAccessIsr, dispatch]);

  useEffect(() => {
    if (!canAccessIsr) {
      return;
    }
    dispatch(fetchIsrList(isrCommissionTypeKey));
  }, [canAccessIsr, dispatch, isrCommissionTypeKey]);

  useEffect(() => {
    if (!canAccessIsr) {
      return;
    }
    if (activeTab === "salesConfig") {
      dispatch(fetchSalesConfigList());
    }
    if (activeTab === "salesGoal") {
      dispatch(fetchSalesGoalList());
    }
  }, [activeTab, canAccessIsr, dispatch]);

  useEffect(() => {
    setIsrList(isrAgencyList);
  }, [isrAgencyList]);

  const isrNameById = useCallback((isrId: number) => {
    return isrList.find((isr) => isr.isrId === isrId)?.fullName || "—";
  }, [isrList]);

  const toOptionalAgencyField = (value?: string) => {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? undefined : trimmed;
  };

  const handleIsrSubmit = async (data: IsrFormValues) => {
    const payload: CreateSearchAgency = {
      agencyId: selectedIsr?.isrId,
      agencyName: data.agencyName?.trim(),
      email: data.email?.trim(),
      phone: toOptionalAgencyField(data.phone),
      streetAddress: toOptionalAgencyField(data.streetAddress),
      unitNumber: toOptionalAgencyField(data.unitNumber),
      zipcode: toOptionalAgencyField(data.zipcode),
      city: toOptionalAgencyField(data.city),
      state: toOptionalAgencyField(data.state),
      address: undefined,
      inactiveDate: undefined,
      commissionType: isrCommissionTypeKey,
      agencyProductCommisions: [],
    };
    const response = selectedIsr
      ? await dispatch(editAgency(payload))
      : await dispatch(saveAgency(payload));
    if (response.meta.requestStatus === "fulfilled" && response.payload?.code === SUCCESS_CODE) {
      dispatch(setSingleAlertObj({
        message: selectedIsr ? "ISR updated successfully." : "ISR saved successfully.",
        alertType: "success",
      }));
      dispatch(fetchIsrList(isrCommissionTypeKey));
      setIsrPopupOpen(false);
      setSelectedIsr(null);
    }
  };

  const handleSalesGoalSubmit = async (data: SalesGoalFormValues) => {
    const isSalesGoalEdit = !!selectedSalesGoal;
    const actualCollectedRaw = String(data.actualCollectedAmt ?? "").trim();
    const achievementPctRaw = String(data.achievementPct ?? "").trim();
    const payload = {
      ...(isSalesGoalEdit ? { salesGoalId: selectedSalesGoal!.salesGoalId } : {}),
      year: Number(data.year),
      month: data.month,
      invoiceYear: Number(data.invoiceYear),
      collectionTarget: Number(data.collectionTarget),
      ...(isSalesGoalEdit
        ? {
            actualCollectedAmt: actualCollectedRaw !== "" ? Number(actualCollectedRaw) : null,
            achievementPct: achievementPctRaw !== "" ? Number(achievementPctRaw) : null,
          }
        : {}),
    };
    const response = selectedSalesGoal
      ? await dispatch(updateSalesGoal(payload))
      : await dispatch(saveSalesGoal(payload));
    if (response.meta.requestStatus === "fulfilled" && response.payload?.code === SUCCESS_CODE) {
      dispatch(setSingleAlertObj({
        message: selectedSalesGoal ? "Sales Goal updated successfully." : "Sales Goal saved successfully.",
        alertType: "success",
      }));
      dispatch(fetchSalesGoalList());
      setSalesGoalPopupOpen(false);
      setSelectedSalesGoal(null);
      return;
    }
    dispatch(setSingleAlertObj({
      message: getIsrSaveErrorMessage(response, "Unable to save Sales Goal."),
      alertType: "error",
    }));
  };

  const getSalesConfigRecord = useCallback(
    (salesConfigId: number) => salesConfigList.find((item) => item.salesConfigId === salesConfigId) || null,
    [salesConfigList]
  );

  const buildTargetRatesPayload = (rows: SalesConfigTargetRatesFormValues["targetRates"]) =>
    rows
      .filter((row) => String(row.targetRange ?? "").trim() !== "" || String(row.targetCompPercent ?? "").trim() !== "")
      .map((row) => ({
        targetRange: String(row.targetRange).trim(),
        targetCompPercent: Number(String(row.targetCompPercent).trim()),
        active: row.active !== false,
      }));

  const handleSalesConfigTargetRatesSubmit = async (data: SalesConfigTargetRatesFormValues) => {
    const config = selectedSalesConfigForTarget;
    if (!config) {
      return;
    }
    const targetRates = buildTargetRatesPayload(data.targetRates);
    if (targetRates.length === 0) {
      dispatch(setSingleAlertObj({
        message: "Enter at least one target range and comp %.",
        alertType: "error",
      }));
      return;
    }
    const seenRanges = new Set<string>();
    let hasActiveRate = false;
    for (const rate of targetRates) {
      const rangeValidation = validateSalesConfigTargetRange(rate.targetRange);
      if (rangeValidation !== true) {
        dispatch(setSingleAlertObj({ message: rangeValidation, alertType: "error" }));
        return;
      }
      const rangeKey = rate.targetRange.toLowerCase();
      if (seenRanges.has(rangeKey)) {
        dispatch(setSingleAlertObj({
          message: "Duplicate target range is not allowed.",
          alertType: "error",
        }));
        return;
      }
      seenRanges.add(rangeKey);
      const compValidation = validateCompPercent(String(rate.targetCompPercent), `Comp % (${rate.targetRange})`);
      if (compValidation !== true) {
        dispatch(setSingleAlertObj({ message: compValidation, alertType: "error" }));
        return;
      }
      if (rate.active !== false) {
        hasActiveRate = true;
      }
    }
    if (!hasActiveRate) {
      dispatch(setSingleAlertObj({
        message: "Enter at least one active target range and comp %.",
        alertType: "error",
      }));
      return;
    }
    const overlapListValidation = validateSalesConfigTargetRangeListNoOverlap(
      targetRates.map((rate) => rate.targetRange)
    );
    if (overlapListValidation !== true) {
      dispatch(setSingleAlertObj({ message: overlapListValidation, alertType: "error" }));
      return;
    }
    const payload = {
      salesConfigId: config.salesConfigId,
      isrId: config.isrId,
      invoiceYear: Number(config.invoiceYear),
      effectiveFrom: config.effectiveFrom,
      effectiveTo: config.effectiveTo || undefined,
      alignment: config.alignment,
      scope: config.scope,
      rateType: "TARGET_BASED",
      targetRates,
    };
    const response = await dispatch(updateSalesConfig(payload));
    if (response.meta.requestStatus === "fulfilled" && response.payload?.code === SUCCESS_CODE) {
      dispatch(setSingleAlertObj({
        message: "Target based rates saved successfully.",
        alertType: "success",
      }));
      await dispatch(fetchSalesConfigList());
      return;
    }
    dispatch(setSingleAlertObj({
      message: getIsrSaveErrorMessage(response, "Unable to save target based rates."),
      alertType: "error",
    }));
  };

  const handleSalesConfigSubmit = async (data: SalesConfigFormValues) => {
    const standardTrimmed = String(data.standardCompPercent ?? "").trim();
    const isStandard = standardTrimmed !== "";

    if (isStandard) {
      const standardValidation = validateCompPercent(standardTrimmed, "Standard Comp %");
      if (standardValidation !== true) {
        dispatch(setSingleAlertObj({ message: standardValidation, alertType: "error" }));
        return;
      }
    }

    const existingTargetRates = selectedSalesConfig?.targetRates
      ?.filter((rate) => String(rate.targetRange ?? "").trim() !== "" && String(rate.targetCompPercent ?? "").trim() !== "")
      .map((rate) => ({
        targetRange: String(rate.targetRange).trim(),
        targetCompPercent: Number(String(rate.targetCompPercent).trim()),
        active: rate.active !== false,
      })) || [];

    const payload = {
      ...(selectedSalesConfig ? { salesConfigId: selectedSalesConfig.salesConfigId } : {}),
      isrId: Number(data.isrId),
      invoiceYear: Number(data.invoiceYear),
      effectiveFrom: data.effectiveFrom ? dayjs(data.effectiveFrom).format("YYYY-MM-DD") : "",
      effectiveTo: data.effectiveTo ? dayjs(data.effectiveTo).format("YYYY-MM-DD") : undefined,
      alignment: data.alignment,
      scope: data.scope,
      rateType: isStandard ? "STANDARD" : "TARGET_BASED",
      ...(isStandard
        ? { standardCompPercent: Number(standardTrimmed), targetRates: [] }
        : { targetRates: existingTargetRates }),
    };
    const response = selectedSalesConfig
      ? await dispatch(updateSalesConfig(payload))
      : await dispatch(saveSalesConfig(payload));
    if (response.meta.requestStatus === "fulfilled" && response.payload?.code === SUCCESS_CODE) {
      dispatch(setSingleAlertObj({
        message: selectedSalesConfig ? "Sales Config updated successfully." : "Sales Config saved successfully.",
        alertType: "success",
      }));
      dispatch(fetchSalesConfigList());
      setSalesConfigPopupOpen(false);
      setSelectedSalesConfig(null);
      return;
    }
    dispatch(setSingleAlertObj({
      message: getIsrSaveErrorMessage(response, "Unable to save Sales Config."),
      alertType: "error",
    }));
  };

  const confirmDelete = (message: string, onConfirm: () => void) => {
    setOptionPopup({
      message,
      show: true,
      variant: "confirm",
      buttons: [
        { name: "Yes", color: "primary", onClick: onConfirm },
        { name: "No", color: "error", onClick: () => setOptionPopup({ message: "", show: false }) },
      ],
    });
  };

  const inactivateIsr = async (row: IsrRecord) => {
    const response = await dispatch(inactivateAgencyById(row.isrId));
    setOptionPopup({ message: "", show: false });
    if (response.meta.requestStatus === "fulfilled" && response.payload?.code === SUCCESS_CODE) {
      dispatch(setSingleAlertObj({
        message: "ISR inactivated successfully.",
        alertType: "success",
      }));
      dispatch(fetchIsrList(isrCommissionTypeKey));
    }
  };

  const isrActionComponent = (row: IsrRecord) => (
    <>
      <CustomIconButton
        color="info"
        size="small"
        variant="outlined"
        tooltipMessage="Edit"
        tooltipPlacement="top"
        onClick={() => {
          setSelectedIsr(row);
          setIsrPopupOpen(true);
        }}
      >
        <ModeOutlined fontSize="small" />
      </CustomIconButton>
      {row.active && (
        <CustomIconButton
          color="error"
          size="small"
          variant="outlined"
          tooltipMessage="Inactivate"
          tooltipPlacement="top"
          onClick={() => confirmDelete(
            `Are you sure you want to inactivate ISR "${row.fullName}"?`,
            () => inactivateIsr(row)
          )}
        >
          <DoDisturbAltOutlined fontSize="small" />
        </CustomIconButton>
      )}
    </>
  );

  const openSalesConfigEdit = (salesConfigId: number) => {
    const record = getSalesConfigRecord(salesConfigId);
    if (!record) {
      return;
    }
    setSelectedSalesConfig(record);
    setSalesConfigPopupOpen(true);
  };

  const openSalesConfigTargetRates = (salesConfigId: number) => {
    const record = getSalesConfigRecord(salesConfigId);
    if (!record || record.rateType !== "TARGET_BASED") {
      return;
    }
    setSelectedSalesConfigForTarget(record);
    setSalesConfigTargetPopupOpen(true);
  };

  const salesConfigActionComponent = (row: { salesConfigId: number; rateType?: string }) => (
    <>
      <CustomIconButton
        color="info"
        size="small"
        variant="outlined"
        tooltipMessage="Edit"
        tooltipPlacement="top"
        onClick={() => openSalesConfigEdit(row.salesConfigId)}
      >
        <ModeOutlined fontSize="small" />
      </CustomIconButton>
      {row.rateType === "TARGET_BASED" && (
        <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          tooltipMessage="Target Based Rates"
          tooltipPlacement="top"
          onClick={() => openSalesConfigTargetRates(row.salesConfigId)}
        >
          <TrackChanges fontSize="small" />
        </CustomIconButton>
      )}
    </>
  );

  const activeIsrList = useMemo(() => {
    const active = isrList.filter((isr) => isr.active);
    if (selectedSalesConfig) {
      const selected = isrList.find((isr) => isr.isrId === selectedSalesConfig.isrId);
      if (selected && !active.some((isr) => isr.isrId === selected.isrId)) {
        return [...active, selected];
      }
    }
    return active;
  }, [isrList, selectedSalesConfig]);

  const salesGoalActionComponent = (row: SalesGoalRecord) => (
    <>
      <CustomIconButton
        color="info"
        size="small"
        variant="outlined"
        tooltipMessage="Edit"
        tooltipPlacement="top"
        onClick={() => {
          setSelectedSalesGoal(row);
          setSalesGoalPopupOpen(true);
        }}
      >
        <ModeOutlined fontSize="small" />
      </CustomIconButton>
    </>
  );

  const isrRows = isrList.map((row) => ({
    ...row,
    uniqueId: row.isrId,
    status: row.active ? "ACTIVE" : "INACTIVE",
  }));

  const salesConfigRows = salesConfigList.map((row) => ({
    uniqueId: row.salesConfigId,
    salesConfigId: row.salesConfigId,
    rateType: row.rateType,
    isrName: row.isrName || isrNameById(row.isrId),
    invoiceYear: row.invoiceYear,
    effectiveFromDisplay: formatDate(row.effectiveFrom),
    effectiveToDisplay: formatDate(row.effectiveTo),
    alignment: row.alignment,
    scope: row.scope,
    rateTypeDisplay: row.rateType === "TARGET_BASED" ? "Target Based Rate" : "Standard Rate",
    standardCompDisplay: row.rateType === "STANDARD" ? formatPercent(row.standardCompPercent) : "—",
  }));

  const salesGoalRows = salesGoalList.map((row) => ({
    ...row,
    uniqueId: row.salesGoalId,
    collectionTargetDisplay: formatCurrency(row.collectionTarget),
    actualCollectedAmtDisplay: formatCurrency(row.actualCollectedAmt),
    achievementPctDisplay: row.achievementPct !== "" ? `${row.achievementPct}%` : "—",
    invoiceYearDisplay: row.invoiceYear || "—",
  }));

  const renderSectionCard = (
    title: string,
    icon: React.ReactNode,
    buttonLabel: string,
    onCreate: () => void,
    subtitle?: string,
    children?: React.ReactNode,
    headerActions?: React.ReactNode
  ) => (
    <Box
      sx={{
        borderRadius: "12px",
        border: `1px solid ${modalTokens.border}`,
        backgroundColor: modalTokens.bg,
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={iconBoxSx(modalTokens.accent)}>{icon}</Box>
          <Box>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: modalTokens.textPrimary, letterSpacing: "-0.01em" }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: "0.78rem", color: modalTokens.textSecondary, mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          {headerActions}
          <MuiButton
            type="button"
            variant="contained"
            disableElevation
            startIcon={<AddCircleOutlineOutlined sx={{ fontSize: "1rem !important" }} />}
            onClick={onCreate}
            sx={primaryActionBtnSx}
          >
            {buttonLabel}
          </MuiButton>
        </Box>
      </Box>
      {children}
    </Box>
  );

  if (isrAccessReady && !canAccessIsr) {
    return <AccessRestrictedComponent />;
  }

  return (
    <Box mx={2} mb={5} mt={2}>
      <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
        <IconButton
          onClick={() => navigate(-1)}
          aria-label="Back"
          sx={{
            border: `1px solid ${modalTokens.border}`,
            borderRadius: "10px",
            color: modalTokens.textSecondary,
            "&:hover": { color: modalTokens.accent, borderColor: modalTokens.accent, backgroundColor: modalTokens.inputBg },
          }}
        >
          <ArrowBackOutlined fontSize="small" />
        </IconButton>
        <Header
          title="ISR Dashboard"
          subtitle="Add ISRs and manage sales config and sales goals."
        />
      </Stack>

      <ProductTabNav
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as IsrTabId)}
      />

      {activeTab === "isr" && renderSectionCard(
        "ISR List",
        <PersonOutlined sx={{ fontSize: 20, color: modalTokens.accent }} />,
        "Add ISR",
        () => {
          setSelectedIsr(null);
          setIsrPopupOpen(true);
        },
        undefined,
        <CustomTable
          columns={[
            { id: "fullName", label: "ISR Name" },
            { id: "email", label: "Email" },
            { id: "status", label: "Status" },
            { id: "action", label: "Action" },
          ]}
          rows={isrRows}
          actionComponent={(row) => isrActionComponent(row as IsrRecord)}
          onRowClick={(row) => {
            setSelectedIsr(row as IsrRecord);
            setIsrPopupOpen(true);
          }}
          disablePagination
          emptyText={fetchIsrListStatus === "loading" ? "Loading..." : "No ISR(s) Found"}
          showMenu
          uniqueValue="isrId"
          sx={tableSx}
        />
      )}

      {activeTab === "salesConfig" && renderSectionCard(
        "Sales Config List",
        <TuneOutlined sx={{ fontSize: 20, color: modalTokens.accent }} />,
        "Add Sales Config",
        () => {
          setSelectedSalesConfig(null);
          setSalesConfigPopupOpen(true);
        },
        undefined,
        <CustomTable
          columns={[
            { id: "isrName", label: "ISR Name" },
            { id: "invoiceYear", label: "Invoice Year" },
            { id: "effectiveFromDisplay", label: "Effective From" },
            { id: "effectiveToDisplay", label: "Effective To" },
            { id: "alignment", label: "Alignment" },
            { id: "scope", label: "Scope" },
            { id: "rateTypeDisplay", label: "Rate Type" },
            { id: "standardCompDisplay", label: "Standard % of Paid Invoices" },
            { id: "action", label: "Action" },
          ]}
          rows={salesConfigRows}
          actionComponent={(row) => salesConfigActionComponent(row)}
          onRowClick={(row) => openSalesConfigEdit(row.salesConfigId)}
          disablePagination
          emptyText={fetchSalesConfigListStatus === "loading" ? "Loading..." : "No Sales Config(s) Found"}
          showMenu
          uniqueValue="salesConfigId"
          sx={tableSx}
        />
      )}

      {activeTab === "salesGoal" && renderSectionCard(
        "Sales Goal List",
        <TrackChanges sx={{ fontSize: 20, color: modalTokens.accent }} />,
        "Add Sales Goal",
        () => {
          setSelectedSalesGoal(null);
          setSalesGoalPopupOpen(true);
        },
        undefined,
        <CustomTable
          columns={[
            { id: "year", label: "Payment Year" },
            { id: "month", label: "Payment Month" },
            { id: "invoiceYearDisplay", label: "Invoice Year" },
            { id: "collectionTargetDisplay", label: "Collection Target" },
            { id: "actualCollectedAmtDisplay", label: "Actual Collected Amt" },
            { id: "achievementPctDisplay", label: "Achievement %" },
            { id: "action", label: "Action" },
          ]}
          rows={salesGoalRows}
          actionComponent={(row) => salesGoalActionComponent(row as SalesGoalRecord)}
          onRowClick={(row) => {
            setSelectedSalesGoal(row as SalesGoalRecord);
            setSalesGoalPopupOpen(true);
          }}
          disablePagination
          emptyText={fetchSalesGoalListStatus === "loading" ? "Loading..." : "No Sales Goal(s) Found"}
          showMenu
          uniqueValue="salesGoalId"
          sx={tableSx}
        />
      )}

      <IsrPopup
        open={isrPopupOpen}
        isrRecord={selectedIsr}
        onClose={() => {
          setIsrPopupOpen(false);
          setSelectedIsr(null);
        }}
        onSubmitForm={handleIsrSubmit}
      />
      <SalesConfigPopup
        open={salesConfigPopupOpen}
        isrList={activeIsrList}
        salesConfig={selectedSalesConfig}
        onClose={() => {
          setSalesConfigPopupOpen(false);
          setSelectedSalesConfig(null);
        }}
        onSubmitForm={handleSalesConfigSubmit}
      />
      <SalesConfigTargetRatesPopup
        open={salesConfigTargetPopupOpen}
        salesConfig={selectedSalesConfigForTarget}
        onClose={() => {
          setSalesConfigTargetPopupOpen(false);
          setSelectedSalesConfigForTarget(null);
        }}
        onSubmitForm={handleSalesConfigTargetRatesSubmit}
        onValidationError={(message) => dispatch(setSingleAlertObj({ message, alertType: "error" }))}
      />
      <SalesGoalPopup
        open={salesGoalPopupOpen}
        salesGoal={selectedSalesGoal}
        onClose={() => {
          setSalesGoalPopupOpen(false);
          setSelectedSalesGoal(null);
        }}
        onSubmitForm={handleSalesGoalSubmit}
      />
      <OptionsPopup
        open={optionPopup.show}
        onClose={() => setOptionPopup({ message: "", show: false })}
        variant={optionPopup.variant}
        message={optionPopup.message}
        buttons={optionPopup.buttons}
      />
    </Box>
  );
};

export default IsrManagement;
